import {
  getMaxColorCount,
  getTotalQuantity,
  sizeIsDisabled,
  useDesign,
  useProduct,
  useProjectItems,
} from '@customink/project-service-react-components';
import type {
  ProjectItem,
  ProjectItemDraft,
  SizeQuantity,
} from '@customink/project-service-react-components';
import { SAMPLE_TYPES } from '@customink/project-service-types/dist/project.interface';
import { useCallback } from 'react';

import {
  HIGH_COLOR_DESIGN_COLOR_COUNT,
  HIGH_COLOR_DESIGN_MIN_ORDER_QUANTITY,
} from '@/constants';

export const useHasOutOfStockSizes = (projectItem: ProjectItem) => {
  const itemSizes = projectItem.sizes || {};
  const selectedSizes = Object.keys(itemSizes);
  const { data: product } = useProduct({
    colorId: projectItem.product?.colorId,
  });
  const selectedColor = product?.colors?.find(
    (color) => color.id === projectItem.product?.colorId,
  );
  return !!product?.sizes?.some(
    (size) =>
      sizeIsDisabled(size, selectedColor?.sizes) &&
      selectedSizes.includes(size),
  );
};

export const EditFormWcibmo = {
  MISSING_QUANTITY: 'MISSING_QUANTITY',
  BELOW_COLOR_MIN: 'BELOW_COLOR_MIN',
  BELOW_ORDER_MIN: 'BELOW_ORDER_MIN',
  BELOW_MIN_WITH_ALTERNATIVE: 'BELOW_MIN_WITH_ALTERNATIVE',
  TOO_MANY_SAMPLES: 'TOO_MANY_SAMPLES',
  HIGH_COLOR_MIN: 'HIGH_COLOR_MIN',
  HIGH_COLOR_MIN_OR_PRODUCT: 'HIGH_COLOR_MIN_OR_PRODUCT',
  HIGH_COLOR_MIN_OR_STYLE: 'HIGH_COLOR_MIN_OR_STYLE',
} as const;
export type EditFormWcibmoType =
  (typeof EditFormWcibmo)[keyof typeof EditFormWcibmo];

type UseProjectItemSizeValidatorArgs = {
  colorId?: number;
  designCid?: string;
  projectItemId?: string;
  allowZeroQuantity?: boolean;
};
export const useProjectItemSizeValidator = ({
  colorId,
  designCid,
  projectItemId,
  allowZeroQuantity = false,
}: UseProjectItemSizeValidatorArgs) => {
  const { data: projectItems } = useProjectItems();
  const { data: product } = useProduct({
    colorId,
  });
  const { data: design } = useDesign(designCid);
  const colorCount = getMaxColorCount(design);

  const selectedColor = product?.colors?.find((color) => color.id === colorId);

  const hasAnyPrintedSampleItems = projectItems?.some(
    (projectItem) =>
      projectItem?.sampleType === SAMPLE_TYPES.PLA_PRINTED_SAMPLE,
  );

  const projectGroupOtherItems = projectItems?.filter(
    (groupItem) =>
      groupItem.design?.cid === designCid && groupItem.id !== projectItemId,
  );
  const projectGroupOtherSizes = projectGroupOtherItems
    ?.map((groupItem) => groupItem.sizesQuantities)
    .flatMap((sizes) => sizes)
    .filter((sizes) => !!sizes) as SizeQuantity[];

  const isSinglesEnabled = product?.has_singles_enabled_colors;
  const hasSinglesSelected = selectedColor?.singles_enabled;
  const hasSinglesAlternative = product?.singles_alternative_id;
  const minimumQuantityFromProduct = hasSinglesSelected
    ? product?.minimum_quantity
    : product?.minimum_bulk_quantity;

  const validator = useCallback(
    (projectItem: ProjectItemDraft) => {
      const totalItemQuantity = getTotalQuantity(projectItem.sizesQuantities);

      const totalGroupQuantity =
        getTotalQuantity(projectGroupOtherSizes) + totalItemQuantity;

      const isBelowMin =
        minimumQuantityFromProduct &&
        totalGroupQuantity < minimumQuantityFromProduct;

      if (hasAnyPrintedSampleItems && totalItemQuantity > 1) {
        return EditFormWcibmo.TOO_MANY_SAMPLES;
      }

      if (allowZeroQuantity) {
        return null;
      }

      if (totalItemQuantity < 1) {
        return EditFormWcibmo.MISSING_QUANTITY;
      }

      if (isBelowMin && isSinglesEnabled && !hasSinglesSelected) {
        return EditFormWcibmo.BELOW_COLOR_MIN;
      }

      if (isBelowMin && hasSinglesAlternative) {
        return EditFormWcibmo.BELOW_MIN_WITH_ALTERNATIVE;
      }

      if (isBelowMin) {
        return EditFormWcibmo.BELOW_ORDER_MIN;
      }

      if (
        !selectedColor?.dtg_enabled &&
        colorCount >= HIGH_COLOR_DESIGN_COLOR_COUNT &&
        totalGroupQuantity < HIGH_COLOR_DESIGN_MIN_ORDER_QUANTITY
      ) {
        if (hasSinglesAlternative) {
          if (product?.colors?.some((color) => color.dtg_enabled)) {
            return EditFormWcibmo.HIGH_COLOR_MIN_OR_PRODUCT;
          }
          return EditFormWcibmo.HIGH_COLOR_MIN_OR_STYLE;
        }
        return EditFormWcibmo.HIGH_COLOR_MIN;
      }

      return null;
    },
    [
      isSinglesEnabled,
      hasSinglesSelected,
      hasSinglesAlternative,
      minimumQuantityFromProduct,
      projectGroupOtherSizes,
      hasAnyPrintedSampleItems,
    ],
  );

  return validator;
};
